import { transition } from '@helpers/animate';
import brand from '@helpers/brand';
import { fluid } from '@helpers/fluid';
import fonts from '@helpers/fonts';
import { Device, from, until } from '@helpers/media';
import { h3, h4 } from '@helpers/typography';
import styled from 'styled-components';
const Container = styled.article `
  &[data-type='office'] {
    height: 100%;
  }
`;
const Link = styled.a `
  --cardBgColor: ${brand.tertiary.nearlyWhite.light};
  --cardFgColor: ${brand.primary.purple.base};
  --cardTextColor: ${brand.primary.base};

  [data-type='publication'] & {
    --cardBgColor: ${brand.global.white};
    --cardFgColor: ${brand.primary.purple.base};
    --cardTextColor: ${brand.primary.base};
    --cardBorderColor: ${brand.tertiary.nearlyWhite.light};

    border: 2px solid var(--cardBorderColor);
    ${transition('border-color', 0.15, 'easeOut')};
  }

  [data-type='people'] & {
    --cardBgColor: transparent;
    --cardFgColor: ${brand.primary.purple.base};
    --cardTextColor: ${brand.primary.base};
  }

  &:is(a):hover,
  &:is(a):focus-visible,
  [data-featured='true'] & {
    --cardBgColor: ${brand.primary.purple.base};
    --cardFgColor: ${brand.global.white};
    --cardTextColor: ${brand.global.white};

    [data-type='publication'] & {
      --cardBgColor: ${brand.global.white};
      --cardFgColor: ${brand.primary.purple.base};
      --cardTextColor: ${brand.primary.base};
      --cardBorderColor: ${brand.primary.purple.base};
    }

    [data-type='people'] & {
      --cardBgColor: transparent;
      --cardFgColor: ${brand.primary.purple.base};
      --cardTextColor: ${brand.primary.base};
    }

    [data-type='video'] & {
      ${() => IconHolder} svg {
        &:nth-child(2) {
          display: block;
        }

        &:nth-child(1) {
          display: none;
        }
      }
    }
  }

  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
  height: 100%;
  background-color: var(--cardBgColor);
  color: var(--cardFgColor);
  text-decoration: none;
  padding: 20px 15px;
  border-bottom-right-radius: 50px;
  ${transition('background-color color', 0.15, 'easeOut')};

  [data-type='office'] & {
    padding: 4px 30px 30px;
  }

  @media ${from(Device.TabletLarge)} {
    [data-featured='true'] & {
      display: grid;
      align-items: flex-start;
      grid-template-columns: 1fr 1fr;
      padding-inline: 25px;
    }
  }

  @media ${until(Device.TabletLarge)} {
    [data-featured='true'] & {
      position: relative;
    }
  }
`;
const FeaturedText = styled.div `
  ${fonts.ubuntu.bold}
  font-size: ${fluid(16, 22)};
  line-height: 1.5em;

  @media ${from(Device.TabletLarge)} {
    [data-featured='true'] & {
      grid-column: 1;
    }
  }
`;
const Snippets = styled.div `
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 4px;

  @media ${from(Device.TabletLarge)} {
    [data-featured='true'] & {
      flex-direction: row;
      grid-column: 2;
      gap: 10px;
    }
  }

  @media ${until(Device.TabletLarge)} {
    [data-featured='true'] & {
      position: static;
    }
  }
`;
const DetailedSnippet = styled.div `
  --iconSize: 18px;

  display: flex;
  flex-direction: row;
  align-items: center;
  font-size: 0.86em;
  gap: 8px;

  svg {
    width: var(--iconSize);
    height: var(--iconSize);
  }
`;
const Date = styled(DetailedSnippet) ``;
const Time = styled(DetailedSnippet) ``;
const Location = styled(DetailedSnippet) ``;
const IconHolder = styled.div `
  --iconSize: 24px;

  position: absolute;
  top: 0;
  right: 0;

  @media ${until(Device.TabletLarge)} {
    [data-featured='true'] & {
      top: 16px;
      right: 16px;
    }
  }

  svg {
    width: var(--iconSize);
    height: var(--iconSize);

    &:nth-child(2) {
      display: none;
    }
  }
`;
const ImageWrapper = styled.div `
  margin-bottom: 20px;

  [data-type='people'] & {
    max-width: 333px;
  }

  img {
    display: block;
    width: 100%;
    aspect-ratio: 1 / 1;
    object-fit: cover;
    object-position: center;

    [data-type='people'] & {
      border-radius: 50%;
      border: 10px solid ${brand.tertiary.nearlyWhite.light};
    }

    [data-type='office'] & {
      max-height: 210px;
    }
  }
`;
const Title = styled.h3 `
  ${h4}
  padding-left: 15px;
  padding-right: 4px;
  margin: 0;
  border-left: 4px solid var(--cardFgColor);

  &:is(h2) {
    ${h3}
    margin: 0;
  }

  [data-type='publication'] & {
    --cardFgColor: ${brand.secondary.pink.light};
  }

  [data-type='people'] &,
  [data-type='office'] & {
    border: none;
    padding-left: 0;
  }

  @media ${from(Device.TabletLarge)} {
    [data-featured='true'] & {
      grid-column: 1;
      max-width: 90%;
    }
  }
`;
const Description = styled.p `
  color: var(--cardTextColor);

  [data-type='office'] & {
    margin-bottom: 1em;
  }

  address {
    font-style: normal;
  }

  a {
    color: initial;
  }

  @media ${from(Device.TabletLarge)} {
    [data-featured='true'] & {
      grid-column: 2;
    }
  }
`;
const CtaWrapper = styled.div `
  margin-top: auto;

  [data-type='text-only']:not(a) {
    --colorPrimary: var(--cardFgColor);
    --colorContrast: var(--cardFgColor);
  }

  @media ${from(Device.TabletLarge)} {
    [data-featured='true'] & {
      grid-column: 2;
      margin-left: auto;
      margin-right: 12px;
    }
  }
`;
export default {
    Container,
    Link,
    FeaturedText,
    Snippets,
    Date,
    Time,
    Location,
    IconHolder,
    ImageWrapper,
    Title,
    Description,
    CtaWrapper,
};
